import {
  createFileRoute,
  Navigate,
  Outlet,
  useLocation,
} from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { PanelLeft } from 'lucide-react'
import apiClient from '@/api/client'
import type { User } from '@/types'
import {
  AdminSidebar,
  adminNavTitleFromPath,
  MOBILE_ADMIN_HEADER_HEIGHT_CLASS,
} from '@/components/admin/AdminSidebar'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  canAccessAdminRoute,
  defaultAdminPath,
  isStaffRole,
} from '@/lib/staff-roles'
import { isEmbeddedServerMode } from '@/lib/bhooklyNative'
import { useModuleVisibility } from '@/hooks/useModuleVisibility'
import { moduleForAdminPath } from '@/lib/modules'

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
})

function AdminLayout() {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const location = useLocation()
  // Phone-only drawer threshold: tablets (≥768) get the persistent rail so the
  // 768-1024 band is desktop-class, not a blown-up phone (responsive overhaul).
  const isNarrowViewport = useMediaQuery('(max-width: 767px)')
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const modules = useModuleVisibility()

  useEffect(() => {
    const storedUser = localStorage.getItem('pos_user')
    const token = localStorage.getItem('pos_token')

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser)
        setUser(parsedUser)
      } catch (error) {
        console.error('Failed to parse stored user:', error)
        localStorage.removeItem('pos_user')
        localStorage.removeItem('pos_token')
      }
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isNarrowViewport) setMobileNavOpen(false)
  }, [isNarrowViewport])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-muted-foreground">Loading…</p>
        </div>
      </div>
    )
  }

  if (!apiClient.isAuthenticated() || !user) {
    return <Navigate to="/login" />
  }

  if (!isStaffRole(user.role)) {
    return <Navigate to="/login" replace />
  }

  if (!canAccessAdminRoute(user.role, location.pathname)) {
    return <Navigate to={defaultAdminPath(user.role, modules.isHidden)} replace />
  }

  // Modules this deployment hides (ENABLED_MODULES) redirect like a role
  // denial. isHidden stays false while settings load, so a full deployment
  // is never bounced off a deep link during that first fetch.
  const pathModule = moduleForAdminPath(location.pathname)
  if (pathModule && modules.isHidden(pathModule)) {
    return <Navigate to={defaultAdminPath(user.role, modules.isHidden)} replace />
  }

  /** Checkout counter manages its own scroll regions (menu + rail); never page-scroll `<main>`. */
  const isCheckoutCounterRoute = location.pathname === '/admin/counter'
  const embeddedServer = isEmbeddedServerMode()
  const hideChrome =
    embeddedServer &&
    (location.pathname === '/admin/server' || location.pathname.startsWith('/admin/server/'))

  // Height/offset yield to the desktop update strip when one is showing
  // (F-FE-N47). Both resolve to 0px in a browser, so this is a no-op there.
  // Without the subtraction the shell would be 100dvh BELOW a 40px banner and
  // the counter would start page-scrolling, which breaks its layout.
  return (
    <div className="mt-[var(--desktop-update-banner-h)] flex h-[calc(100dvh_-_var(--desktop-update-banner-h))] overflow-hidden bg-background">
      {!hideChrome && (
        <div
          className={
            isNarrowViewport ? 'w-0 shrink-0 overflow-visible' : 'min-h-0 shrink-0'
          }
        >
          <AdminSidebar
            user={user}
            isNarrowViewport={isNarrowViewport}
            narrowDrawerOpen={mobileNavOpen}
            onNarrowDrawerOpenChange={setMobileNavOpen}
          />
        </div>
      )}
      <main
        className={cn(
          'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
          !isCheckoutCounterRoute && 'md:overflow-y-auto'
        )}
      >
        {!hideChrome && (
          <header
            className={`sticky top-0 z-[60] flex shrink-0 items-center gap-3 border-b border-border bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden ${MOBILE_ADMIN_HEADER_HEIGHT_CLASS} pt-[env(safe-area-inset-top,0px)]`}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11 shrink-0 touch-manipulation"
              aria-label={
                mobileNavOpen ? 'Navigation menu open' : 'Open navigation menu'
              }
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen((o) => !o)}
            >
              <PanelLeft className="h-6 w-6" aria-hidden />
            </Button>
            <span className="min-w-0 flex-1 truncate text-base font-semibold">
              {adminNavTitleFromPath(location.pathname)}
            </span>
          </header>
        )}
        <div
          className={cn(
            'min-h-0 flex-1',
            isCheckoutCounterRoute
              ? 'flex flex-col overflow-hidden md:flex md:min-h-0 md:flex-1 md:overflow-hidden'
              : 'overflow-y-auto md:contents'
          )}
        >
          <Outlet />
        </div>
      </main>
    </div>
  )
}
